import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiUrl } from '../utils/Api';

const useDetailPage = ({ blogCount }) => {
    const { id, idTwo } = useParams();
    const [post, setPost] = useState(null);
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tableOfContents, setTableOfContents] = useState([]);
    const [contentId, setContentId] = useState();
    const contentRef = useRef(null);
    const navigate = useNavigate();

    // Fetch single post
    useEffect(() => {
        const fetchPost = async (slug) => {
            try {
                setLoading(true);
                const response = await fetch(`${ApiUrl}posts?slug=${slug}`);
                const data = await response.json();

                if (data.length > 0) {
                    setPost(data[0]);
                    window.scrollTo(0, 0);
                } else {
                    setPost(null);
                }
            } catch (error) {
                console.error('Error fetching post:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPost(id);
    }, [id]);

    // Fetch related posts
    useEffect(() => {
        const fetchPosts = async (categoryId = null) => {
            try {
                const categoryFilter = categoryId ? `&categories=${categoryId}` : '';
                const response = await fetch(`${ApiUrl}posts?per_page=${blogCount}&page=1${categoryFilter}`);
                if (!response.ok) throw new Error('Failed to fetch posts');

                const data = await response.json();
                setPosts(data);
            } catch (error) {
                console.error('Error fetching posts:', error);
            }
        };

        if (idTwo !== 'null') fetchPosts(idTwo);
        else fetchPosts();
    }, [idTwo]);

    // Generate Table of Contents
    useEffect(() => {
        if (post && post.content?.rendered) {
            setTimeout(() => {
                const headings = contentRef.current?.querySelectorAll('h2, h3, h4, h5, h6');
                if (headings && headings.length > 0) {
                    const toc = Array.from(headings).map((heading) => {
                        const extractText = (node) => {
                            let text = '';
                            for (const childNode of node.childNodes) {
                                if (childNode.nodeType === Node.TEXT_NODE) text += childNode.textContent;
                                else if (childNode.nodeType === Node.ELEMENT_NODE) text += extractText(childNode);
                            }
                            return text;
                        };

                        const text = extractText(heading).trim();
                        return {
                            id: heading.id || `toc-${Math.random().toString(36).substring(2, 11)}`,
                            text,
                            level: parseInt(heading.tagName.substring(1)),
                        };
                    });

                    headings.forEach((heading, index) => {
                        if (!heading.id) heading.id = toc[index].id;
                    });

                    setTableOfContents(toc);
                }
            }, 1000);
        }
    }, [post]);

    const scrollToHeading = (id) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setContentId(id);
    };

    const singleBlog = (slug) => {
        window.scrollTo(0, 0);
        navigate(`/blogdetail/${slug}/${idTwo || 'null'}`);
    };

    return { post, posts, loading, tableOfContents, contentRef, contentId, scrollToHeading, singleBlog };
};

export default useDetailPage;
